import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contact Justine Sacco | Speaking, Advisory & Press Inquiries';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Get in touch with Justine Sacco for speaking engagements, advisory work, or press inquiries. Chief Communications Officer and strategic communications expert.');

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'ContactPage',
          '@id': `${SITE_URL}/contact`,
          url: `${SITE_URL}/contact`,
          name: 'Contact Justine Sacco | Speaking, Advisory & Press Inquiries',
          description: 'Get in touch with Justine Sacco for speaking engagements, advisory work, or press inquiries. Chief Communications Officer and strategic communications expert.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          dateModified: '2026-04-02',
          inLanguage: 'en-US',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
            ],
          },
          mainEntity: { '@id': `${SITE_URL}/#person` },
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-contact');
      if (existing) existing.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6f5gmonin4q71a31g00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 
            className="text-6xl md:text-8xl mb-12"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
          >
            CONTACT
          </h1>
          <p 
            className="text-2xl text-gray-300 mb-16"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            For speaking, advisory, or press inquiries.
          </p>

          <form 
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit} 
            className="space-y-8"
          >
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-3 text-gray-400">
                NAME
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-[#FF4D00] outline-none px-6 py-4 text-lg transition-colors"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-3 text-gray-400">
                EMAIL
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-[#FF4D00] outline-none px-6 py-4 text-lg transition-colors"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-3 text-gray-400">
                MESSAGE
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-[#FF4D00] outline-none px-6 py-4 text-lg resize-none transition-colors"
              />
              <p className="text-sm text-gray-500 mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="bg-[#FF4D00] hover:bg-[#FF6B2C] text-white font-bold text-lg px-12 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {status === 'submitting' ? 'SENDING...' : 'SEND MESSAGE'}
            </button>

            {status === 'success' && (
              <div className="bg-green-900/20 border border-green-500 text-green-400 px-6 py-4">
                Message sent successfully. I'll be in touch.
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-900/20 border border-red-500 text-red-400 px-6 py-4">
                Something went wrong. Please try again or reach out via LinkedIn.
              </div>
            )}
          </form>

          <div className="mt-16 pt-16 border-t border-zinc-800">
            <a 
              href="https://www.linkedin.com/in/justinesacco" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-xl font-medium text-[#FF4D00] hover:text-[#FF6B2C] transition-colors cursor-pointer"
            >
              <i className="ri-linkedin-fill text-2xl mr-3 w-8 h-8 flex items-center justify-center"></i>
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
